import React, { useState } from 'react';
import { Gauge, Calendar, Ruler, StickyNote, FileText } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FileUploadField from '../ui/FileUploadField';
import { formatDateForAPI } from '../../utils/dateUtils';
import { useCreateReading } from '../../hooks/useReadings';
import { useAllServices } from '../../hooks/useServices';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const AddReadingModal = ({ isOpen, onClose, service = null }) => {
    const initServiceKey = service ? `${service.supplierUid}::${service.uid}` : '';

    const [formData, setFormData] = useState({
        serviceKey: initServiceKey,
        value: '',
        unit: '',
        readAt: '',
        notes: '',
        fileUid: null,
    });

    const [isUploading, setIsUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const createReading = useCreateReading();
    const { t } = useLanguage();
    const { data: allServices = [], isLoading: loadingServices } = useAllServices();

    const validateForm = () => {
        const newErrors = {};

        if (!service && !formData.serviceKey) {
            newErrors.serviceKey = t(KEYS.INVOICE_FIELD_SERVICE_REQUIRED);
        }

        if (!formData.value) {
            newErrors.value = t(KEYS.READING_FIELD_VALUE_REQUIRED);
        } else if (isNaN(parseFloat(formData.value)) || parseFloat(formData.value) < 0) {
            newErrors.value = t(KEYS.READING_FIELD_VALUE_REQUIRED);
        }

        if (!formData.readAt) {
            newErrors.readAt = t(KEYS.READING_FIELD_DATE_REQUIRED);
        }

        if (formData.unit && formData.unit.length > 50) {
            newErrors.unit = 'Maximum 50 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const emptyForm = () => ({
        serviceKey: initServiceKey,
        value: '',
        unit: '',
        readAt: '',
        notes: '',
        fileUid: null,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');

        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            const activeServiceKey = service ? `${service.supplierUid}::${service.uid}` : formData.serviceKey;
            const [, serviceUid] = activeServiceKey.split('::')

            const data = {
                value: parseFloat(formData.value),
                read_at: formatDateForAPI(formData.readAt),
            };
            if (formData.unit) data.unit = formData.unit;
            if (formData.notes) data.notes = formData.notes;
            if (formData.fileUid) data.file_uid = formData.fileUid;

            await createReading.mutateAsync({ serviceUid, data });

            setFormData(emptyForm());
            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.READING_ADD_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setFormData(emptyForm());
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.READING_ADD_TITLE)}
            subtitle="Record a new meter or consumption value"
            size="medium"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button
                        variant="primary"
                        size="medium"
                        onClick={handleSubmit}
                        disabled={isSubmitting || (!service && loadingServices) || isUploading}
                    >
                        {isSubmitting ? t(KEYS.READING_ADD_SUBMITTING) : t(KEYS.READING_ADD_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 bg-status-overdue-bg text-status-overdue rounded text-sm mb-4">
                    {submitError}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
                {/* Service — read-only when pre-selected, dropdown otherwise */}
                {service ? (
                    <div className="space-y-1">
                        <label className="label-base flex items-center gap-1.5">
                            <FileText className="w-4 h-4" />
                            Service
                        </label>
                        <div className="input-base bg-surface-overlay text-text-muted cursor-not-allowed">
                            {service.name || service.type}{service.supplierName ? ` — ${service.supplierName}` : ''}
                        </div>
                    </div>
                ) : (
                    <div className="space-y-1">
                        <label htmlFor="serviceKey" className="label-base flex items-center gap-1.5">
                            <FileText className="w-4 h-4" />
                            Service <span className="text-red-500">*</span>
                        </label>
                        {loadingServices ? (
                            <div className="text-sm text-text-muted text-center py-2">
                                {t(KEYS.INVOICE_FIELD_LOADING_SERVICES)}
                            </div>
                        ) : (
                            <select
                                id="serviceKey"
                                name="serviceKey"
                                value={formData.serviceKey}
                                onChange={handleChange}
                                className={`input-base ${errors.serviceKey ? 'border-red-500 focus:ring-red-500' : ''}`}
                                disabled={isSubmitting}
                            >
                                <option value="">{t(KEYS.INVOICE_FIELD_SELECT_SERVICE)}</option>
                                {allServices.map((svc) => (
                                    <option
                                        key={svc.uid}
                                        value={`${svc.supplierUid}::${svc.uid}`}
                                    >
                                        {svc.name || svc.type} — {svc.supplierName}
                                    </option>
                                ))}
                            </select>
                        )}
                        {errors.serviceKey && (
                            <span className="text-xs text-red-500 mt-1">{errors.serviceKey}</span>
                        )}
                    </div>
                )}

                {/* Value */}
                <div className="space-y-1">
                    <label htmlFor="value" className="label-base flex items-center gap-1.5">
                        <Gauge className="w-4 h-4" />
                        Value <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="number"
                        id="value"
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        className={`input-base ${errors.value ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., 1234.56"
                        step="0.01"
                        min="0"
                        disabled={isSubmitting}
                    />
                    {errors.value && (
                        <span className="text-xs text-red-500 mt-1">{errors.value}</span>
                    )}
                </div>

                {/* Unit */}
                <div className="space-y-1">
                    <label htmlFor="unit" className="label-base flex items-center gap-1.5">
                        <Ruler className="w-4 h-4" />
                        Unit
                    </label>
                    <input
                        type="text"
                        id="unit"
                        name="unit"
                        value={formData.unit}
                        onChange={handleChange}
                        className={`input-base ${errors.unit ? 'border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="e.g., kWh, m³, L"
                        maxLength={50}
                        disabled={isSubmitting}
                    />
                    {errors.unit && (
                        <span className="text-xs text-red-500 mt-1">{errors.unit}</span>
                    )}
                </div>

                {/* Read At */}
                <div className="space-y-1">
                    <label htmlFor="readAt" className="label-base flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        Read Date <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        id="readAt"
                        name="readAt"
                        value={formData.readAt}
                        onChange={handleChange}
                        className={`input-base ${errors.readAt ? 'border-red-500 focus:ring-red-500' : ''}`}
                        disabled={isSubmitting}
                    />
                    {errors.readAt && (
                        <span className="text-xs text-red-500 mt-1">{errors.readAt}</span>
                    )}
                </div>

                {/* Notes */}
                <div className="space-y-1">
                    <label htmlFor="notes" className="label-base flex items-center gap-1.5">
                        <StickyNote className="w-4 h-4" />
                        Notes
                    </label>
                    <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        className="input-base resize-y"
                        placeholder="Additional notes (optional)"
                        rows={3}
                        disabled={isSubmitting}
                    />
                </div>

                {/* File Upload */}
                {(service?.uid || formData.serviceKey) && (
                    <FileUploadField
                        label="Meter Photo / Document"
                        serviceUid={service?.uid ?? formData.serviceKey.split('::')[1]}
                        value={formData.fileUid}
                        onChange={(uid) => setFormData((prev) => ({ ...prev, fileUid: uid }))}
                        onUploadingChange={(uploading) => setIsUploading(uploading)}
                        disabled={isSubmitting}
                    />
                )}
            </form>
        </Modal>
    );
};

export default AddReadingModal;
